import { useEffect, useState } from 'react';
import { useActiveProject, useService } from '../../../../../hooks';
import { useAsync } from 'react-use';
import { AxiosResponse } from 'axios';
import {
  AttachmentSearchData,
  buildAttachmentSearchQuery,
} from './buildAttachmentSearchQuery';
import {
  generateQuestionnaireSearchQuery,
  QuestionnaireSearchData,
} from './buildQuestionnaireSearchQuery';
import { Attachment, PRIMARY_SURVEY_ID_SYSTEM } from './Attachment';
import {
  useReportErrorToBugsnag,
  useReportInfoToBugsnag,
} from '../../../../../util/bugsnagReporting';
import FhirSearchService from '../../../../../services/FhirSearchService';

const fss = new FhirSearchService();

type QuestionnaireHit = QuestionnaireSearchData['hits']['hits'][number];

const getPrimarySurveyId = (
  meta: { tag?: { system?: string; code?: string }[] } | undefined,
): string | undefined =>
  meta?.tag?.find(tag => tag.system === PRIMARY_SURVEY_ID_SYSTEM)?.code;

export const useAttachmentArbiterState = (attachments?: string[]) => {
  const [project] = useActiveProject();
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [hasEverLoaded, setHasEverLoaded] = useState(false);
  const [attachmentMap, setAttachmentMap] = useState<
    Record<string, Attachment>
  >({});
  const [questionnairesByPrimaryId, setQuestionnairesByPrimaryId] = useState<
    Record<string, QuestionnaireHit>
  >({});
  const [fssSearch] = useService(fss.search);

  const reportErrorToBugsnag = useReportErrorToBugsnag();
  const reportInfoToBugsnag = useReportInfoToBugsnag();

  const { error } = useAsync(async () => {
    if (attachments === undefined || attachmentsLoading) {
      return;
    }

    const toFetch = attachments.filter(ref => !attachmentMap[ref]);
    if (toFetch.length === 0) {
      if (!hasEverLoaded) {
        setHasEverLoaded(true);
      }
      return;
    }

    setAttachmentsLoading(true);
    try {
      const attachmentSearchResponse: AxiosResponse<AttachmentSearchData> =
        await fssSearch(
          project.id,
          buildAttachmentSearchQuery(toFetch),
          undefined,
          { queryId: 'ai-tools-attachment-search' },
        );

      // Collect any primary-survey-ids referenced by the new hits that we have
      // not already fetched questionnaire metadata for.
      const newPrimaryIds = Array.from(
        new Set(
          attachmentSearchResponse.data.hits.hits
            .map(hit => getPrimarySurveyId(hit._source.meta))
            .filter(
              (id): id is string => !!id && !questionnairesByPrimaryId[id],
            ),
        ),
      );

      const newQuestionnaireHits: QuestionnaireHit[] = newPrimaryIds.length
        ? (
            (await fssSearch(
              project.id,
              generateQuestionnaireSearchQuery(newPrimaryIds),
              undefined,
              { queryId: 'ai-tools-questionnaire-search' },
            )) as AxiosResponse<QuestionnaireSearchData>
          ).data.hits.hits
        : [];

      const mergedQuestionnaires: Record<string, QuestionnaireHit> = {
        ...questionnairesByPrimaryId,
      };
      newQuestionnaireHits.forEach(hit => {
        const id = getPrimarySurveyId(hit._source.meta);
        if (id) {
          mergedQuestionnaires[id] = hit;
        }
      });

      const ctx = {
        questionnaireData: {
          hits: { hits: Object.values(mergedQuestionnaires) },
        },
      };

      const newEntries: Record<string, Attachment> = {};
      attachmentSearchResponse.data.hits.hits.forEach(hit => {
        const attachment = Attachment.from(ctx, hit._source);
        newEntries[attachment.identifier] = attachment;
      });

      setQuestionnairesByPrimaryId(mergedQuestionnaires);
      // Merge with existing entries last-wins for previous values so that
      // `addAttachment`-injected `TemporaryAttachment`s survive a delta fetch
      // even if the same reference is later returned by FSS.
      setAttachmentMap(prev => ({ ...newEntries, ...prev }));
      setHasEverLoaded(true);

      const missing = toFetch.filter(ref => !newEntries[ref]);
      if (missing.length) {
        reportInfoToBugsnag(
          `Was not able to load all attachments: ${missing.join(', ')}`,
        );
      }
    } finally {
      setAttachmentsLoading(false);
    }
  }, [attachments, fssSearch, project.id]);

  useEffect(() => {
    if (error) {
      reportErrorToBugsnag(error);
    }
  }, [error, reportErrorToBugsnag]);

  return {
    loading: attachmentsLoading || !hasEverLoaded,
    attachmentMap,
    setAttachmentMap,
  };
};
